import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { LANGUAGES } from '../../utils/constants';
import Colors from '../../utils/color';
import { HITL3_BAR_CONTROL_HEIGHT } from '../../utils/hitl3Spacing';

// HITL-1665: the selected segment takes the header's own colour, so the switch
// belongs to the same console as the header and the Order Flow pills instead of
// carrying a fourth blue. Legacy keeps its original rgb(0, 107, 166) — this
// component is shared, and only the 7.0 bar is being retuned.
const COMPACT_ACTIVE_BACKGROUND = Colors.daintree;
const LEGACY_ACTIVE_BACKGROUND = 'rgb(0, 107, 166)';

// HITL-1665: the compact switch's own metrics, as inline style so they beat the
// shared `classes` the caller passes in (those are sized for the wide
// English / Spanish switch and cannot be changed without moving legacy too).
//
// The 16px gap between the segments is `gap={2}` from the legacy switch, where
// it separates two full words. Between two-letter labels it is just a hole, and
// it was the widest remaining piece of dead space in a row that has to hold one
// line at 1024px.
// The track's own 20px radius belongs to the wide pill-shaped switch; around
// two 4px segments it reads as a lozenge with square contents inside it, so it
// squares off to 6px to sit beside the intent pills.
const COMPACT_SWITCH_STYLE: React.CSSProperties = {
  gap: '2px',
  borderRadius: '6px',
  // 34, down from the shared class's 40. That 6px was a deliberate hit-area
  // bonus ("a bit larger than the intent pills"), but it made the switch stand
  // proud of every control beside it in a row that is meant to read as one
  // line. Same token the intent pills, Staff and Issue use.
  height: HITL3_BAR_CONTROL_HEIGHT,
};

// 4px, matching the intent pills. The shared `languageOption` uses an 18px
// radius, which reads as a pill only while the segment is ~75px wide; once the
// labels became EN / ES the segments narrowed to ~20px and that radius rendered
// them as ellipses. Padding gives the two letters room to sit in a rounded rect
// instead of being pinched by their own corners.
const COMPACT_OPTION_STYLE: React.CSSProperties = {
  borderRadius: '4px',
  padding: '0 8px',
};

// HITL-1665: two-letter segment labels for the compact bottom bar. The full
// language name moves to the tooltip rather than being dropped — worth ~60px,
// which is a sixth of what the row needed to hold one line at 1024px.
const SHORT_LABEL: Record<string, string> = {
  [LANGUAGES.ENGLISH]: 'EN',
  [LANGUAGES.SPANISH]: 'ES',
};

const titleCase = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

interface LanguageSwitchProps {
  classes: any;
  isSpanish: boolean;
  onIntentUp: (intentCard: any) => void;
  intentCard: any;
  /**
   * Render the segments as EN / ES with the full name in a tooltip.
   *
   * Opt-in, and off by default, because this component is shared with the
   * legacy IntentsSection — which is not part of HITL 7.0 and must keep
   * rendering English / Spanish. The caller supplies the flag; this component
   * deliberately does not read it, so there is one gate per surface.
   */
  compact?: boolean;
}

const LanguageSwitch: React.FC<LanguageSwitchProps> = ({
  classes,
  isSpanish,
  onIntentUp,
  intentCard,
  compact = false,
}) => {
  const renderLanguageOption = (language: string, isActive: boolean) => {
    const option = (
      <div
        className={`${classes.languageOption} ${
          isActive
            ? classes.languageOptionActive
            : classes.languageOptionInactive
        }`}
        onMouseUp={() => onIntentUp(intentCard)}
        // Inline, not the class: `languageOptionActive` sets the same property
        // and this wins, so changing only the class would have no effect.
        style={{
          backgroundColor: isActive
            ? compact
              ? COMPACT_ACTIVE_BACKGROUND
              : LEGACY_ACTIVE_BACKGROUND
            : 'transparent',
          color: isActive ? 'white' : '#4f5153',
          ...(compact ? COMPACT_OPTION_STYLE : {}),
        }}
      >
        {compact ? SHORT_LABEL[language] ?? language : language}
      </div>
    );
    // The tooltip only exists to carry the name the short label drops, so it is
    // not added in the full-label case where it would just repeat the segment.
    // Title-cased here rather than left to CSS: the segment gets its capital
    // from `textTransform` on `languageOption`, which a tooltip never sees, so
    // the raw 'english' / 'spanish' would otherwise surface on hover.
    return compact ? (
      <Tooltip title={titleCase(language)} arrow disableInteractive>
        {option}
      </Tooltip>
    ) : (
      option
    );
  };

  return (
    <Box
      className={classes.languageSwitch}
      display="flex"
      gap={2}
      style={compact ? COMPACT_SWITCH_STYLE : undefined}
    >
      {renderLanguageOption(LANGUAGES.ENGLISH, !isSpanish)}
      {renderLanguageOption(LANGUAGES.SPANISH, isSpanish)}
    </Box>
  );
};

export default LanguageSwitch;
